/**
 * (c) 2021 - 2022, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { Button } from "@chakra-ui/button";
import { HStack, Link, List, ListItem, Text, VStack } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { GenericDialog } from "../../common/GenericDialog";
import { FinalFocusRef } from "../../project/project-actions";

export const enum ConnectHelpChoice {
  Next,
  NextDontShowAgain,
  Cancel,
}

interface ConnectHelpDialogProps {
  callback: (choice: ConnectHelpChoice) => void;
  dialogNormallyHidden: boolean;
  shownByRequest: boolean;
  finalFocusRef: FinalFocusRef;
}

const ConnectDialog = ({
  callback,
  dialogNormallyHidden,
  shownByRequest,
  finalFocusRef,
}: ConnectHelpDialogProps) => {
  return (
    <GenericDialog
      finalFocusRef={finalFocusRef}
      onClose={() => callback(ConnectHelpChoice.Cancel)}
      body={<ConnectDialogBody />}
      footer={
        <ConnectDialogFooter
          onClose={() => callback(ConnectHelpChoice.Cancel)}
          onNext={() => callback(ConnectHelpChoice.Next)}
          onNextDontShowAgain={() =>
            callback(ConnectHelpChoice.NextDontShowAgain)
          }
          dialogNormallyHidden={dialogNormallyHidden}
          shownByRequest={shownByRequest}
        />
      }
      size="lg"
    />
  );
};

/**
 * Simple text instructions for connecting over WebUSB.
 * Replaces the previous illustrated, multi-step walkthrough.
 */
const ConnectDialogBody = () => {
  return (
    <VStack
      width="100%"
      p={5}
      pb={0}
      spacing={4}
      alignItems="flex-start"
    >
      <Text as="h2" fontSize="xl" fontWeight="semibold">
        <FormattedMessage id="connect-help-title" />
      </Text>
      <Text fontSize="lg">
        <FormattedMessage id="connect-hover" />
      </Text>
      <List as="ol" styleType="decimal" stylePosition="inside" spacing={2}>
        <ListItem fontSize="lg">
          <FormattedMessage id="connect-cable-title" />
        </ListItem>
        <ListItem fontSize="lg">
          <FormattedMessage id="connect-help-one" />
        </ListItem>
        <ListItem fontSize="lg">
          <FormattedMessage id="connect-help-two" />
        </ListItem>
      </List>
    </VStack>
  );
};

interface ConnectDialogFooterProps {
  onClose: () => void;
  onNext: () => void;
  onNextDontShowAgain: () => void;
  dialogNormallyHidden: boolean;
  shownByRequest: boolean;
}

const ConnectDialogFooter = ({
  onClose,
  onNext,
  onNextDontShowAgain,
  dialogNormallyHidden,
  shownByRequest,
}: ConnectDialogFooterProps) => {
  return (
    <HStack
      spacing={2.5}
      width={dialogNormallyHidden || shownByRequest ? "auto" : "100%"}
    >
      {!dialogNormallyHidden && !shownByRequest && (
        <Link
          onClick={onNextDontShowAgain}
          as="button"
          color="brand.500"
          mr="auto"
        >
          <FormattedMessage id="dont-show-again" />
        </Link>
      )}
      <Button onClick={onClose} size="lg">
        <FormattedMessage id="cancel-action" />
      </Button>
      <Button onClick={onNext} variant="solid" size="lg">
        <FormattedMessage id="next-action" />
      </Button>
    </HStack>
  );
};

export default ConnectDialog;
